import { html, nothing } from "lit";
import { AGENTS_EDITOR_HINTS } from "../control-hints.ts";
import { icons } from "../icons.ts";

export type AgentsEditorMode = "add";

export type AgentsEditorModalProps = {
  open: boolean;
  mode: AgentsEditorMode;
  saving: boolean;
  error: string | null;
  draft: {
    id: string;
    name: string;
    workspace: string;
    setDefault: boolean;
  };
  onClose: () => void;
  onDraftChange: (patch: Partial<AgentsEditorModalProps["draft"]>) => void;
  onSubmit: () => void;
};

export function renderAgentsEditorModal(props: AgentsEditorModalProps) {
  if (!props.open) {
    return nothing;
  }

  return html`
    <div
      class="agents-editor-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agents-editor-title"
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
    >
      <div class="agents-editor-card" @click=${(e: Event) => e.stopPropagation()}>
        <header class="agents-editor-header">
          <div>
            <h2 id="agents-editor-title" class="agents-editor-title">Add agent</h2>
            <p class="agents-editor-sub">Adds to agents.list and saves gateway config.</p>
          </div>
          <button
            type="button"
            class="btn icon"
            title=${AGENTS_EDITOR_HINTS.close}
            aria-label="Close"
            @click=${props.onClose}
          >
            ${icons.x}
          </button>
        </header>
        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : nothing
        }
        <div class="agents-editor-form">
          <label class="field">
            <span>Agent id</span>
            <input
              class="mono"
              .value=${props.draft.id}
              placeholder="e.g. research"
              @input=${(e: Event) =>
                props.onDraftChange({ id: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label class="field">
            <span>Display name</span>
            <input
              .value=${props.draft.name}
              placeholder="Optional label"
              @input=${(e: Event) =>
                props.onDraftChange({ name: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label class="field">
            <span>Workspace path</span>
            <input
              class="mono"
              .value=${props.draft.workspace}
              placeholder="Optional, e.g. ~/agents/research"
              @input=${(e: Event) =>
                props.onDraftChange({ workspace: (e.target as HTMLInputElement).value })}
            />
          </label>
          <label class="field agents-editor-checkbox">
            <input
              type="checkbox"
              .checked=${props.draft.setDefault}
              @change=${(e: Event) =>
                props.onDraftChange({ setDefault: (e.target as HTMLInputElement).checked })}
            />
            <span>Set as instance default</span>
          </label>
        </div>
        <div class="agents-editor-actions">
          <button
            type="button"
            class="btn"
            ?disabled=${props.saving}
            title=${AGENTS_EDITOR_HINTS.cancel}
            aria-label=${AGENTS_EDITOR_HINTS.cancel}
            @click=${props.onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn primary"
            ?disabled=${props.saving || !props.draft.id.trim()}
            title=${AGENTS_EDITOR_HINTS.submit}
            aria-label=${AGENTS_EDITOR_HINTS.submit}
            @click=${props.onSubmit}
          >
            ${props.saving ? "Saving…" : "Add agent"}
          </button>
        </div>
      </div>
    </div>
  `;
}
